import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, Spacer, TwoColGrid } from "../Layout/Layout";
import useUser from "../../core/user/useUser";
import { notifyApiErrors } from "../../core/helpers/helpers";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";
import {
  IKLObservationIdentifierNew,
  IObservationIdentifier,
} from "../../core/api/observationIdentifiers/types";
import {
  resolveEditError,
  updateKLObservationIdentifier,
} from "../../core/api/observationIdentifiers/identifiers";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import ObservationIdentifierRow from "./ObservationIdentifierRow";
import { FormProvider, useForm } from "react-hook-form";
import Divider from "../../ui-lib/components/Divider/Divider";
import { getDefaultDropdownItems, getDefaultRowValues } from "./helpers";

const EditObservationIdentifierModal = ({
  isOpen,
  locationId,
  observationIdentifier,
  onClose,
  onUpdate,
}: {
  isOpen: boolean;
  locationId?: number | string;
  observationIdentifier?: IObservationIdentifier;
  onClose: () => void;
  onUpdate: () => void;
}) => {
  const { t } = useTranslation();
  const { authenticatedRequest } = useUser();

  const [loading, setLoading] = useState(false);
  const defaultValues = getDefaultRowValues(observationIdentifier);

  const methods = useForm<IKLObservationIdentifierNew>({
    defaultValues,
  });

  const { selectedRoom, selectedOrganization, selectedLocation } =
    getDefaultDropdownItems(t, observationIdentifier);

  const onSubmit = async (values: IKLObservationIdentifierNew) => {
    setLoading(true);
    try {
      await updateKLObservationIdentifier(
        observationIdentifier?.id || "",
        values,
        authenticatedRequest,
      );
      notify({
        message: t("Locations:oi_update_success"),
      });
      onUpdate();
      onClose();
    } catch (error: any) {
      const apiError = resolveEditError(error);
      if (apiError) {
        methods.setError(apiError.field, { ...apiError });
      }
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    methods.reset(defaultValues);
  }, [observationIdentifier, methods.reset]);

  return (
    <PrimeModal
      withFooter
      withHeader
      loading={loading}
      header={t("Locations:oi_edit_identifier_title")}
      fixedWidth="848px"
      isOpen={isOpen}
      onClose={onClose}
      submitBtn={{
        text: t("Common:save_changes"),
        onClick: methods.handleSubmit(onSubmit),
      }}
      cancelBtn={{ text: t("Common:cancel"), onClick: onClose }}
    >
      <form>
        <Column type="top" align="start">
          <p>{t("Locations:oi_edit_identifier_description")}</p>
          <Spacer size={16} />
          {selectedOrganization && selectedLocation && (
            <>
              <TwoColGrid columnGap={32}>
                <Dropdown
                  disabled={true}
                  title={t("Locations:oi_labels_location_type")}
                  id="locationType"
                  items={[selectedRoom]}
                  selectedItem={selectedRoom}
                  onSelectItem={() => {}}
                />
                <Dropdown
                  required
                  title={t("Locations:oi_labels_organization")}
                  id="organization"
                  disabled={true}
                  items={[selectedOrganization]}
                  selectedItem={selectedOrganization}
                  onSelectItem={() => {}}
                />
              </TwoColGrid>
              <Spacer size={16} />
              <TwoColGrid columnGap={32}>
                <Dropdown
                  required
                  disabled={true}
                  title={t("Locations:oi_labels_room_description")}
                  id="roomDescription"
                  items={[selectedLocation]}
                  selectedItem={selectedLocation}
                  onSelectItem={() => {}}
                />
              </TwoColGrid>
              <Spacer size={16} />
              <Divider />
              <Spacer size={16} />
            </>
          )}
          <FormProvider {...methods}>
            <ObservationIdentifierRow<IKLObservationIdentifierNew> />
          </FormProvider>
        </Column>
      </form>
    </PrimeModal>
  );
};

export default EditObservationIdentifierModal;
